import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Images, Camera } from "@phosphor-icons/react";
import { api, photoImageUrl } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const CATEGORIES = ["Toutes", "Courts", "Équipes", "Ambiance"];

export default function Galerie() {
    const { data: photos = [], isLoading } = useQuery({ queryKey: ["photos"], queryFn: async () => (await api.get("/photos")).data });
    const [cat, setCat] = useState("Toutes");
    const filtered = cat === "Toutes" ? photos : photos.filter((p) => p.category === cat);

    return (
        <div data-testid="galerie-page">
            <PageHeader
                testid="galerie-header"
                overline="En images"
                title="Galerie du club"
                subtitle="Les courts, les équipes et l'ambiance du Tennis Club de La Turballe."
            />

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
                <div className="flex flex-wrap gap-2 mb-8" data-testid="galerie-filters">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            data-testid={`galerie-filter-${c}`}
                            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                                cat === c ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {isLoading ? (
                    <p className="text-slate-500 font-mono text-sm">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <div className="brand-card p-12 text-center" data-testid="galerie-empty">
                        <Camera weight="duotone" size={44} className="mx-auto text-slate-300 mb-4" />
                        <p className="font-display font-bold text-navy text-lg">Les photos arrivent bientôt</p>
                        <p className="text-slate-500 text-sm mt-1">Cet espace accueillera prochainement les photos du club.</p>
                    </div>
                ) : (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
                        {filtered.map((p, i) => (
                            <motion.figure
                                key={p.id}
                                data-testid="photo-card"
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: (i % 6) * 0.05 }}
                                className="mb-4 break-inside-avoid brand-card overflow-hidden group"
                            >
                                <div className="overflow-hidden bg-slate-100">
                                    <img
                                        src={photoImageUrl(p.id)}
                                        alt={p.title || "Photo du club"}
                                        loading="lazy"
                                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                {(p.title || p.description) && (
                                    <figcaption className="p-4">
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-clay">{p.category}</span>
                                        {p.title && <div className="font-display font-bold text-navy mt-1">{p.title}</div>}
                                        {p.description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{p.description}</p>}
                                    </figcaption>
                                )}
                            </motion.figure>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
